import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Button, Spinner, Typography } from 'seagull';
import ContactSupportButton from '~/components/ContactSupportButton';
import { useEventReporter } from '~/event-reporter';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { useStore, useStoreActions } from '~/store/store-context';
import { ErrorType } from '../VendorOnboardingModal.utils';
import styles from './SavingStep.module.scss';
var SavingState = /*#__PURE__*/ function(SavingState) {
    SavingState["Idle"] = "idle";
    SavingState["Saving"] = "saving";
    SavingState["Error"] = "error";
    SavingState["Success"] = "success";
    return SavingState;
}(SavingState || {});
export function SavingStep(props) {
    const { vobData, onSavingSuccess, onErrorBackButton } = props;
    const userInfo = useStore((state)=>state.userInfo);
    const [savingState, setSavingState] = useState("idle");
    const [errorType, setErrorType] = useState();
    const [failureMsg, setFailureMsg] = useState('Something unexpected happened');
    const { api, setUserInfo, setAuthToken, updateUserClientData, fetchUserInfo } = useStoreActions();
    const { reportToHubspot, reportToAppcues, sendEventToGtm } = useEventReporter();
    const { BUSINESS_DESCRIPTION_ONBOARDING } = useFeatureFlags();
    useEffect(()=>{
        const email = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.email()) || '';
        setSavingState("saving");
        reportToAppcues('form-success', 'onboarding-signed-up');
        reportToHubspot('onboarding_signed_up', _object_spread({
            email
        }, vobData));
        saveVobData().then(()=>fetchUserInfo()).then(()=>{
            sendEventToGtm('onboarding_finished', {
                email
            });
            setSavingState("success");
            onSavingSuccess(true);
        }).catch((err)=>{
            const stripeError = new ErrorResponse(err).parseStripeError();
            if (stripeError) {
                setErrorType(ErrorType.Stripe);
                setFailureMsg(stripeError.message);
            } else {
                setErrorType(ErrorType.General);
            }
            setSavingState("error");
        });
    }, []);
    async function saveVobData() {
        // update user info
        const editUserDetailsRes = await api.auth.editUserDetails({
            phone: vobData.phone,
            name: vobData.name
        });
        setUserInfo((userInfo === null || userInfo === void 0 ? void 0 : userInfo.updateUserDetails(editUserDetailsRes)) || null);
        // update/create vendor business
        const businessData = _object_spread_props(_object_spread({
            name: vobData.companyName
        }, BUSINESS_DESCRIPTION_ONBOARDING && {
            description: vobData.businessDescription || undefined
        }), {
            businessTypes: vobData.businessTypes,
            businessTypeOther: vobData.businessTypeOther || null,
            postalAddress: vobData.postalAddress
        });
        const hasUserBusiness = Boolean(userInfo === null || userInfo === void 0 ? void 0 : userInfo.business());
        if (hasUserBusiness) {
            await api.vendor.update(businessData);
        } else {
            const { authToken } = BUSINESS_DESCRIPTION_ONBOARDING ? await api.vendor.create({
                name: vobData.companyName,
                description: vobData.businessDescription || '',
                postalAddress: vobData.postalAddress
            }) : await api.vendor.createOld(businessData);
            if (authToken != null) {
                await setAuthToken(authToken);
            }
        }
        // update client data
        await updateUserClientData({
            'onboarding-num-clients': parseInt(vobData.numOfInvoices)
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            savingState === "saving" ? /*#__PURE__*/ _jsx(SavingData, {}) : null,
            savingState === "error" ? /*#__PURE__*/ _jsx(ErrorState, {
                onErrorBackButton: onErrorBackButton,
                failureMsg: failureMsg,
                errorType: errorType
            }) : null
        ]
    });
}
function SavingData() {
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.savingContainer,
        "data-testid": "vob-saving",
        children: [
            /*#__PURE__*/ _jsx(Spinner, {}),
            /*#__PURE__*/ _jsx(Typography, {
                children: "Setting up your account"
            })
        ]
    });
}
function ErrorState(props) {
    const { onErrorBackButton, failureMsg, errorType } = props;
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.errorContainer,
        "data-testid": "vob-error",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.content,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        testId: "vob-failure-reason",
                        variant: "h4",
                        children: failureMsg
                    }),
                    /*#__PURE__*/ _jsxs(Typography, {
                        skin: "secondary",
                        children: [
                            "Click",
                            ' ',
                            /*#__PURE__*/ _jsx(Typography, {
                                as: "span",
                                weight: "bold",
                                children: "Back"
                            }),
                            ' ',
                            "to try again or ",
                            /*#__PURE__*/ _jsx(ContactSupportButton, {}),
                            "."
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.footer,
                onClick: ()=>{
                    onErrorBackButton(errorType);
                },
                children: /*#__PURE__*/ _jsx(Button, {
                    testId: "vob-error-back-btn",
                    children: "Back"
                })
            })
        ]
    });
}
